import { useEffect, useRef, useState } from 'react'

// Dependency-free SVG radar / spider chart. One axis per item (here: a branch).
// Draws a current-period polygon and, when compare=true, a dashed previous-period
// polygon over the same axes. Used by the Statistic top graph for short periods.

function fmtShort(n) {
  const v = Number(n) || 0
  if (Math.abs(v) >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`
  if (Math.abs(v) >= 1_000) return `${Math.round(v / 1_000)}k`
  return `${Math.round(v)}`
}
// Tight "nice" ceiling just above the peak (fine steps) so the biggest spoke
// fills most of the radius instead of rounding up to double the peak.
function niceMax(max) {
  if (max <= 0) return 5
  const pow = Math.pow(10, Math.floor(Math.log10(max)))
  const v = [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10].map((m) => m * pow).find((s) => s >= max)
  return v || 10 * pow
}

function fmtFull(n) { return Math.round(Number(n) || 0).toLocaleString('en-US') }

export default function RadarChart({ axes = [], color = '#2563eb', prevColor = '#94a3b8', compare = false, showValue = true, showPrev = true, height = 380, compact = true, emptyText = 'ไม่มีข้อมูล' }) {
  const fmtVal = compact ? fmtShort : fmtFull
  const wrapRef = useRef(null)
  const [width, setWidth] = useState(640)

  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((entries) => { const w = entries[0]?.contentRect?.width; if (w) setWidth(Math.max(320, Math.round(w))) })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  if (!axes.length) return <div className="flex items-center justify-center text-[13px] text-slate-400" style={{ height }}>{emptyText}</div>

  const n = axes.length
  const cx = width / 2, cy = height / 2
  const radius = Math.max(40, Math.min(width, height) / 2 - 86)
  // scale only to the polygons actually shown, so hiding a big series lets the
  // remaining one fill the chart
  const max = niceMax(Math.max(...axes.map((a) => Math.max(showValue ? (a.value || 0) : 0, (compare && showPrev) ? (a.prev || 0) : 0)), 0))
  const ang = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n
  const pt = (i, r) => [cx + r * Math.cos(ang(i)), cy + r * Math.sin(ang(i))]
  const valPt = (i, key) => pt(i, radius * ((axes[i][key] || 0) / max))
  const rings = [0.25, 0.5, 0.75, 1]
  const ringPoly = (r) => axes.map((_, i) => pt(i, r).map((v) => v.toFixed(1)).join(',')).join(' ')
  const seriesPoly = (key) => axes.map((_, i) => valPt(i, key).map((v) => v.toFixed(1)).join(',')).join(' ')

  return (
    <div ref={wrapRef} className="w-full">
      <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} overflow="visible" className="overflow-visible">
        {rings.map((f, i) => (
          <polygon key={i} points={ringPoly(radius * f)} fill={i === rings.length - 1 ? '#f8fafc' : 'none'} stroke="#e8edf3" strokeWidth={1} />
        ))}
        {axes.map((a, i) => { const [ex, ey] = pt(i, radius); return <line key={i} x1={cx} y1={cy} x2={ex} y2={ey} stroke="#e8edf3" strokeWidth={1} /> })}
        {rings.map((f, i) => (
          <text key={`r${i}`} x={cx + 4} y={cy - radius * f} dy="0.32em" className="fill-slate-300" fontSize={9.5}>{fmtShort(max * f)}</text>
        ))}

        {/* previous-period polygon (behind) */}
        {compare && showPrev && (
          <>
            <polygon points={seriesPoly('prev')} fill="none" stroke={prevColor} strokeWidth={2} strokeDasharray="5 4" strokeLinejoin="round" />
            {axes.map((a, i) => { const [x, y] = valPt(i, 'prev'); return <circle key={`p${i}`} cx={x} cy={y} r={2.5} fill="#fff" stroke={prevColor} strokeWidth={1.5} /> })}
          </>
        )}

        {/* current-period polygon */}
        {showValue && (
          <>
            <polygon points={seriesPoly('value')} fill={color} fillOpacity={0.16} stroke={color} strokeWidth={2} strokeLinejoin="round" />
            {axes.map((a, i) => { const [x, y] = valPt(i, 'value'); return <circle key={i} cx={x} cy={y} r={3.5} fill="#fff" stroke={color} strokeWidth={2} /> })}
          </>
        )}

        {/* axis labels — foreignObject so Thai complex script renders via HTML engine */}
        {axes.map((a, i) => {
          const [lx, ly] = pt(i, radius + 16)
          const c = Math.cos(ang(i))
          const anchor = c > 0.25 ? 'start' : c < -0.25 ? 'end' : 'middle'
          const ta = anchor === 'start' ? 'left' : anchor === 'end' ? 'right' : 'center'
          const fow = 132
          const fox = anchor === 'start' ? lx : anchor === 'end' ? lx - fow : lx - fow / 2
          return (
            <foreignObject key={i} x={fox} y={ly - 18} width={fow} height={44}>
              <div style={{ textAlign: ta, lineHeight: '1.35', fontFamily: 'inherit' }}>
                <div style={{ fontSize: '11px', color: '#475569', fontWeight: 500 }}>{a.label}</div>
                <div style={{ fontSize: '11px', fontWeight: 700 }}>
                  {showValue && <span style={{ color }}>{fmtVal(a.value || 0)}</span>}
                  {compare && showPrev && <span style={{ color: prevColor, fontWeight: 500 }}>{' / '}{fmtVal(a.prev || 0)}</span>}
                </div>
              </div>
            </foreignObject>
          )
        })}
      </svg>
    </div>
  )
}
